import { useState, useEffect } from "react";
import { useGameStore } from "../store/gameStore";
import { socketManager } from "../services/socket";

/**
 * BETTING PANEL COMPONENT
 *
 * Allows players to place bets and cashout
 */

export function BettingPanel() {
  const [betAmount, setBetAmount] = useState("10");
  const [autoCashout, setAutoCashout] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const currentRound = useGameStore((state) => state.currentRound);
  const currentBet = useGameStore((state) => state.currentBet);
  const user = useGameStore((state) => state.user);
  const isAuthenticated = useGameStore((state) => state.isAuthenticated);

  const canBet =
    isAuthenticated &&
    currentRound?.phase === "BETTING" &&
    !currentBet &&
    user &&
    parseFloat(betAmount) > 0 &&
    parseFloat(betAmount) <= (user?.balance ?? 0);

  const canCashout =
    currentBet?.status === "active" && currentRound?.phase === "RUNNING";

  // Listen for errors and bet confirmations
  useEffect(() => {
    const unsubscribe = socketManager.on("error", (data) => {
      console.error("[BettingPanel] Error from server:", data.message);
      setError(data.message);
      setLoading(false);
      // Clear error after 5 seconds
      const timer = setTimeout(() => setError(null), 5000);
      return () => clearTimeout(timer);
    });

    return () => unsubscribe();
  }, []);

  // Clear error when placing new bet
  useEffect(() => {
    const unsubscribe = socketManager.on("bet_placed", () => {
      console.log("[BettingPanel] Bet placed successfully!");
      setError(null);
      setLoading(false);
      setBetAmount("10");
      setAutoCashout("");
    });

    return () => unsubscribe();
  }, []);

  const handlePlaceBet = () => {
    if (!canBet) {
      console.warn("[BettingPanel] Cannot place bet:", {
        isAuthenticated,
        phase: currentRound?.phase,
        hasBet: !!currentBet,
        hasUser: !!user,
        betAmount: parseFloat(betAmount),
        balance: user?.balance,
      });
      return;
    }

    console.log("[BettingPanel] Placing bet...", {
      amount: parseFloat(betAmount),
      autoCashout: autoCashout ? parseFloat(autoCashout) : undefined,
      roundId: currentRound?.roundId,
    });

    setLoading(true);
    setError(null);

    const amount = parseFloat(betAmount);
    const auto = autoCashout ? parseFloat(autoCashout) : undefined;

    socketManager.placeBet(amount, auto);
  };

  const handleCashout = () => {
    if (!canCashout || !currentBet) return;

    console.log("[BettingPanel] Cashing out...", {
      betId: currentBet.betId,
      multiplier: currentRound?.currentMultiplier,
    });

    setLoading(true);
    socketManager.cashout(currentBet.betId);
  };

  const handleQuickBet = (multiplier: number) => {
    if (user) {
      const newAmount = Math.min(
        parseFloat(betAmount) * multiplier,
        user.balance,
      );
      setBetAmount(newAmount.toFixed(2));
    }
  };

  if (!isAuthenticated) {
    return (
      <div className="bg-slate-800 rounded-lg p-6 text-center">
        <p className="text-white mb-4">Login to start playing!</p>
        <button
          onClick={() => useGameStore.getState().setShowLoginModal(true)}
          className="px-6 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition"
        >
          Login
        </button>
      </div>
    );
  }

  return (
    <div className="bg-slate-800 rounded-lg p-6 space-y-4">
      {/* Balance */}
      <div className="text-center">
        <div className="text-sm text-gray-400">Balance</div>
        <div className="text-2xl font-bold text-white">
          $
          {typeof user?.balance === "number" && !isNaN(user.balance)
            ? user.balance.toFixed(2)
            : "0.00"}
        </div>
      </div>

      {/* Error Message */}
      {error && (
        <div className="bg-danger/20 border border-danger text-danger px-4 py-3 rounded-lg text-sm">
          <div className="flex items-start gap-2">
            <span className="text-lg font-bold flex-shrink-0">⚠</span>
            <p>{error}</p>
          </div>
        </div>
      )}

      {/* Current Bet Display */}
      {currentBet && (
        <div className="bg-slate-700 rounded-lg p-4">
          <div className="text-sm text-gray-400 mb-2">Current Bet</div>
          <div className="flex justify-between items-center">
            <span className="text-white">${currentBet.amount.toFixed(2)}</span>
            <span
              className={`font-bold ${
                currentBet.status === "active"
                  ? "text-success"
                  : currentBet.status === "cashed_out"
                  ? "text-primary-400"
                  : "text-danger"
              }`}
            >
              {currentBet.status === "active" && "Active"}
              {currentBet.status === "cashed_out" &&
                `Won $${currentBet.payout?.toFixed(2)}`}
              {currentBet.status === "lost" && "Lost"}
            </span>
          </div>
          {currentBet.autoCashoutAt && (
            <div className="text-xs text-gray-400 mt-1">
              Auto @ {currentBet.autoCashoutAt}x
            </div>
          )}
        </div>
      )}

      {/* Betting Form */}
      {!currentBet && currentRound?.phase === "BETTING" && (
        <>
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">
              Bet Amount
            </label>
            <input
              type="number"
              value={betAmount}
              onChange={(e) => setBetAmount(e.target.value)}
              min="1"
              max={user?.balance}
              step="0.01"
              className="w-full px-4 py-2 bg-slate-700 border border-slate-600 text-white rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 transition"
            />

            {/* Quick bet buttons */}
            <div className="grid grid-cols-4 gap-2 mt-2">
              <button
                onClick={() => handleQuickBet(0.5)}
                className="px-2 py-1 bg-slate-700 text-white rounded text-sm hover:bg-slate-600 transition"
              >
                ½
              </button>
              <button
                onClick={() => handleQuickBet(2)}
                className="px-2 py-1 bg-slate-700 text-white rounded text-sm hover:bg-slate-600 transition"
              >
                2×
              </button>
              <button
                onClick={() => setBetAmount(String(user?.balance || 0))}
                className="px-2 py-1 bg-slate-700 text-white rounded text-sm hover:bg-slate-600 transition"
              >
                Max
              </button>
              <button
                onClick={() => setBetAmount("10")}
                className="px-2 py-1 bg-slate-700 text-white rounded text-sm hover:bg-slate-600 transition"
              >
                Reset
              </button>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">
              Auto Cashout (Optional)
            </label>
            <input
              type="number"
              value={autoCashout}
              onChange={(e) => setAutoCashout(e.target.value)}
              min="1.01"
              step="0.01"
              placeholder="e.g. 2.00"
              className="w-full px-4 py-2 bg-slate-700 border border-slate-600 text-white rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 transition"
            />
          </div>

          <button
            onClick={handlePlaceBet}
            disabled={!canBet || loading}
            className={`w-full py-3 rounded-lg font-bold text-white transition ${
              canBet && !loading
                ? "bg-success hover:bg-green-600"
                : "bg-gray-600 cursor-not-allowed opacity-50"
            }`}
          >
            {loading ? (
              <span className="flex items-center justify-center gap-2">
                <span className="w-4 h-4 border-2 border-white/20 border-t-white rounded-full animate-spin" />
                Placing bet...
              </span>
            ) : (
              "Place Bet"
            )}
          </button>
        </>
      )}

      {/* Cashout Button */}
      {canCashout && (
        <button
          onClick={handleCashout}
          disabled={loading}
          className="w-full py-3 rounded-lg font-bold text-white bg-danger hover:bg-red-600 disabled:opacity-50 transition animate-pulse-fast"
        >
          {loading ? (
            <span className="flex items-center justify-center gap-2">
              <span className="w-4 h-4 border-2 border-white/20 border-t-white rounded-full animate-spin" />
              Cashing out...
            </span>
          ) : (
            `Cash Out @ ${currentRound?.currentMultiplier.toFixed(2)}x`
          )}
        </button>
      )}

      {/* Waiting for Betting Phase */}
      {!currentBet && currentRound && currentRound.phase !== "BETTING" && (
        <div className="text-center text-gray-400 py-6 border border-slate-700 rounded-lg">
          <p className="text-sm">
            {currentRound.phase === "RUNNING"
              ? `Round in progress... 🚀 (${currentRound.currentMultiplier?.toFixed(
                  2,
                )}x)`
              : currentRound.phase === "CRASHED"
              ? "Round crashed! ⚠️"
              : "Preparing next round..."}
          </p>
        </div>
      )}
    </div>
  );
}
